/*
 * login Sagas
 *
 * @version 1.0.0
 * @author Pietro
 * @since 13-Sep-2024
 */

import { put, fork, take, call, all } from 'redux-saga/effects'
import { NativeBiometric } from '@capgo/capacitor-native-biometric'
import * as loginActions from 'Store/actions/login'
import * as loginApi from 'Api/login/login'
import { doShowToast } from 'Store/actions/toast'
import { flowToastMapType } from 'Utility/flowToastMap'
import { doHidePopup, doShowPopup } from 'Store/actions/popup'
import { flowPopupMapType } from 'Utility/flowPopupMap'
import formattedMessage from 'Route/Login/modules/languageModule'
import { replace } from 'redux-first-history'
import { nullSafe } from 'Utility/globalMethods'
import appEnvRemap from 'Utility/appEnvRemap'
import askBiometricUsage from 'Utility/askBiometricUsage'
import {
  completeLegacyBiometricMigration,
  disableBiometricRefreshToken,
  enableBiometricRefreshToken,
  readBiometricRefreshToken,
  saveProtectedBiometricRefreshToken
} from 'Utility/biometricRefreshToken'
import { Capacitor } from '@capacitor/core'
import { Preferences } from '@capacitor/preferences'

function * handleLoginError (errorCode) {
  const iamLoginErrorPrefix = process.env.IAM_LOGIN_PREFIX_ERROR
  if (errorCode && errorCode.startsWith(iamLoginErrorPrefix)) {
    const code = errorCode.slice(iamLoginErrorPrefix.length)
    switch (code) {
      case '005':
        yield put(doShowToast(flowToastMapType.ERROR_TOAST, { label: formattedMessage.invalidCredentials }))
        break
      case '006':
        yield put(doShowPopup(flowPopupMapType.BLOCKED_24H_MODAL))
        break
      case '007':
        yield put(doShowPopup(flowPopupMapType.BLOCKED_USER))
        break
      case '009':
        yield put(doShowPopup(flowPopupMapType.SERVICE_NOT_AVAILABLE_REFRESH))
        break
      case '010':
        yield put(doShowToast(flowToastMapType.INFO_TOAST, { label: formattedMessage.invalidRefreshToken }))
        break
      default:
        yield put(doShowPopup(flowPopupMapType.SERVICE_NOT_AVAILABLE))
        break
    }
  } else {
    yield put(doShowPopup(flowPopupMapType.SERVICE_NOT_AVAILABLE))
  }
}

function * getTokenSaga (payload) {
  const params = {
    code: payload.code,
    codeVerifier: window.localStorage.getItem('codeVerifier')
  }
  yield put(loginActions.getToken.request({}, { loading: true }))
  const { error, response } = yield call(loginApi.token, { ...params })
  if (!error) {
    yield put(loginActions.doUserLogin({ accessToken: response.data && response.data.access_token, rememberUserName: payload.rememberUserName }))
    yield put(loginActions.getToken.success(response.data))
  } else {
    const queryParams = new URLSearchParams(location.search)
    const code = queryParams.get('code')
    if (code) {
      window.history.replaceState(null, '', window.location.pathname)
    }
    yield put(loginActions.getToken.failure(error, response))
    yield put(doShowPopup(flowPopupMapType.SERVICE_NOT_AVAILABLE))
  }
  if (window.sessionStorage.getItem('remeberMe')) {
    window.sessionStorage.removeItem('remeberMe')
  }
}

// postLogin
function * userLoginSaga (payload) {
  yield put(loginActions.userLogin.request({}, { loading: true }))
  const isKitStart = window.sessionStorage.getItem('kitstart')
  const { error, response } = yield call(loginApi.userLogin, { accessToken: payload.accessToken, rememberUserName: payload.rememberUserName })
  if (!error && nullSafe(() => response.data && response.data.flow && response.data.flow.length > 0, false)) {
    yield put(loginActions.userLogin.success(response.data))
    window.sessionStorage.removeItem('isAuthorized')
    window.sessionStorage.removeItem('isAuthorizedMos')
    if (Capacitor.isNativePlatform()) {
      if (response.data.rememberMeData) {
        yield call([Preferences, Preferences.set], {
          key: 'user_cookie',
          value: response.data.rememberMeData
        })
      } else {
        yield call([Preferences, Preferences.remove], {
          key: 'user_cookie'
        })
      }
    }
    if (isKitStart) {
      window.location.href = '/kitstart'
    } else if (response.data.flow.length === 2 && response.data.flow.findIndex(state => state.includes('END')) !== -1) {
      window.location.href = '/'
    } else {
      window.location.href = '/login-handler/intro'
    }
  } else {
    // window.sessionStorage.clear()
    yield put(loginActions.userLogin.failure(error, { hideErrorPopup: true, ...response }))
    yield put(loginActions.doLogout({ stayOnPage: true }))
    const errorCode = error && error.data && error.data.errorCode
    if (errorCode === 'CONTRACT_CLOSED') {
      return yield put(replace('courtesy/closed-contract'))
    } else if (errorCode === 'SUSPENDED_SECONDARY_USER') {
      return yield put(replace('courtesy/suspended-user'))
    }
    yield put(doShowPopup(flowPopupMapType.SERVICE_NOT_AVAILABLE))
  }
  // pulire sessionStorage e eliminare kitstart
  if (window.sessionStorage.getItem('kitstart')) {
    window.sessionStorage.removeItem('kitstart')
  }
}

export function * logoutSaga (payload) {
  const stayOnPage = nullSafe(() => payload.stayOnPage, false)
  const clearBiometric = nullSafe(() => payload.clearBiometric, false)
  yield put(loginActions.logout.request({}, { loading: true }))
  const { error, response } = yield call(loginApi.userLogout, {})

  if (!error) {
    yield put(loginActions.logout.success(response.data))
  } else {
    yield put(loginActions.logout.failure(error, { hideErrorPopup: true, ...response }))
  }

  window.sessionStorage.clear()
  const userLanguage = window.localStorage.getItem('userLanguage')
  window.localStorage.clear()
  if (userLanguage) {
    window.localStorage.setItem('userLanguage', userLanguage)
  }

  if (Capacitor.isNativePlatform()) {
    try {
      if (clearBiometric) {
        yield call(disableBiometricRefreshToken)
      }
    } catch (e) {
      console.log('Biometric session cleanup error on logout: ', e)
    }
  }

  if (!stayOnPage) { window.location = appEnvRemap.REDIRECT_PATH_LOGIN }
}

export function * getCardHolderNameSaga () {
  yield put(loginActions.getCardHolderName.request())
  const { error, response } = yield call(loginApi.getCardHolderName, {})

  if (!error) {
    yield put(loginActions.getCardHolderName.success(response.data))
    if (Capacitor.isNativePlatform()) {
      const useBiometric = yield call(askBiometricUsage)
      if (useBiometric && useBiometric !== 'never') {
        const availability = yield call([NativeBiometric, NativeBiometric.isAvailable])
        const isAvailable = typeof availability === 'boolean' ? availability : availability && availability.isAvailable
        if (isAvailable) {
          try {
            const { refreshToken, isLegacyMigration } = yield call(readBiometricRefreshToken)
            const rememberUserName = response.data.userInfoFromCookie && response.data.userInfoFromCookie !== ''
            yield put(loginActions.doRefreshToken({ refreshToken, rememberUserName, isLegacyMigration }))
          } catch (e) {
            console.log('BIOMETRIC ERROR: ', e)
          }
        }
      }
    }
  } else {
    yield put(loginActions.getCardHolderName.failure(error, response))
  }
}
function * iamLoginSaga (payload) {
  const params = {
    username: payload.email,
    password: payload.password,
    captchaToken: payload.captchaToken,
    ...(payload.captchaVersion ? { captchaVersion: payload.captchaVersion } : {})
  }

  yield put(loginActions.iamLogin.request({ ...params }, { loading: true }))

  try {
    yield call(loginApi.iamLogin, { ...params })

    yield put(loginActions.iamLogin.success({}, {}))
  } catch (e) {
    yield put(loginActions.iamLogin.failure({ message: 'IAM Login form submission failed', details: e }))
    yield put(doShowPopup(flowPopupMapType.SERVICE_NOT_AVAILABLE))
  }
}

export function * ropcLoginSaga (payload) {
  const params = {
    username: nullSafe(() => payload.email, ''),
    password: nullSafe(() => payload.password, ''),
    rememberUserName: nullSafe(() => payload.rememberUserName, false)
  }
  yield put(loginActions.ropcToken.request({ ...params }, { loading: true }))
  const { error, response } = yield call(loginApi.ropcToken, { ...params })
  if (!error) {
    yield put(loginActions.ropcToken.success(response.data))
    const sessionHeader = 'x-client-session'
    if (response.headers[sessionHeader]) {
      window.sessionStorage.setItem('x-client-session', response.headers[sessionHeader])
    }
    if (response.data.refresh_token) {
      window.sessionStorage.setItem('refreshToken', response.data.refresh_token)
      const availability = yield call([NativeBiometric, NativeBiometric.isAvailable])
      const isAvailable = typeof availability === 'boolean' ? availability : availability && availability.isAvailable
      if (isAvailable) {
        const dontshowBiometricPopUp = yield call(askBiometricUsage)
        if (dontshowBiometricPopUp !== null && !dontshowBiometricPopUp) {
          yield put(doShowPopup(flowPopupMapType.ASK_BIOMETRIC, { rememberUserName: payload.rememberUserName, refresh_token: response.data.refresh_token }))
        } else {
          if (dontshowBiometricPopUp !== null && dontshowBiometricPopUp === true) {
            try {
              yield call(saveProtectedBiometricRefreshToken, response.data.refresh_token)
            } catch (e) {
              try {
                yield call(disableBiometricRefreshToken)
              } catch (cleanupError) {
                console.log('Biometric preference cleanup error after protected storage failure: ', cleanupError)
              }
            }
          }
          yield put(loginActions.doUserLogin({ rememberUserName: payload.rememberUserName }))
        }
      } else {
        yield put(loginActions.doUserLogin({ rememberUserName: payload.rememberUserName }))
      }
    } else {
      yield put(loginActions.doUserLogin({ rememberUserName: payload.rememberUserName }))
    }
  } else {
    const errorCode = nullSafe(() => error.data.error, null)
    if (errorCode) {
      yield call(handleLoginError, errorCode)
    }
    if (window.sessionStorage.refreshToken) {
      window.sessionStorage.removeItem('refreshToken')
    }
    yield put(loginActions.ropcToken.failure(error, { hideErrorPopup: true, ...response }))
  }
}

export function * refreshTokenSaga (payload) {
  yield put(loginActions.refreshToken.request({ refreshToken: payload.refreshToken }, { loading: true }))
  const { error, response } = yield call(loginApi.ropcToken, { refreshToken: payload.refreshToken, rememberUserName: payload.rememberUserName })
  if (!error) {
    yield put(loginActions.refreshToken.success(response.data))
    window.sessionStorage.setItem('refreshToken', response.data.refresh_token)
    const sessionHeader = 'x-client-session'
    if (response.headers[sessionHeader]) {
      window.sessionStorage.setItem('x-client-session', response.headers[sessionHeader])
    }
    if (response.data.refresh_token) {
      if (typeof payload.isLegacyMigration === 'boolean') {
        const persistRefreshToken = payload.isLegacyMigration
          ? completeLegacyBiometricMigration
          : saveProtectedBiometricRefreshToken
        try {
          yield call(persistRefreshToken, response.data.refresh_token)
        } catch (e) {
          try {
            yield call(disableBiometricRefreshToken)
          } catch (cleanupError) {
            console.log('Biometric preference cleanup error after protected storage failure: ', cleanupError)
          }
        }
      }
    }
    yield put(loginActions.doUserLogin({ rememberUserName: payload.rememberUserName }))
  } else {
    const errorCode = nullSafe(() => error.data.error, null)
    if (errorCode) {
      yield call(handleLoginError, errorCode)
    }
    if (window.sessionStorage.refreshToken) {
      window.sessionStorage.removeItem('refreshToken')
    }
    if (payload.isLegacyMigration === true) {
      try {
        yield call(disableBiometricRefreshToken)
      } catch (cleanupError) {
        console.log('Biometric cleanup error after legacy migration failure: ', cleanupError)
      }
    }
    yield put(loginActions.refreshToken.failure(error, { hideErrorPopup: true, ...response }))
  }
}

export function * choseBiometricOptionSaga (payload) {
  if (payload.choice === 'true') {
    yield call(enableBiometricRefreshToken, payload.refresh_token)
  } else {
    yield call(disableBiometricRefreshToken, payload.choice)
  }
  yield put(loginActions.doUserLogin({ rememberUserName: payload.rememberUserName }))
}

function * extendSessionSaga () {
  yield put(loginActions.extendSession.request({}, { loading: true }))
  const { error, response } = yield call(loginApi.extendSession, {})
  if (!error) {
    yield put(loginActions.extendSession.success(response.data))
  } else {
    yield put(loginActions.extendSession.failure(error, { hideErrorPopup: true, ...response }))
  }
  yield put(doHidePopup())
}

function * getLoginNewsSaga () {
  yield put(loginActions.getLoginNews.request({}, { loading: true }))
  const { error, response } = yield call(loginApi.getLoginNews, {})

  if (!error) {
    yield put(loginActions.getLoginNews.success(response.data))
  } else {
    yield put(loginActions.getLoginNews.failure(error, { hideErrorPopup: true, ...response }))
  }
}

// @generator saga

// @generator saga:watch
function * watchRefreshToken () {
  while (true) {
    const { payload } = yield take(loginActions.actionTypes.DO_REFRESH_TOKEN)
    yield call(refreshTokenSaga, payload)
  }
}

function * watchRopcLogin () {
  while (true) {
    const { payload } = yield take(loginActions.actionTypes.DO_ROPC_TOKEN)
    yield call(ropcLoginSaga, payload)
  }
}

function * watchIamLogin () {
  while (true) {
    const { payload } = yield take(loginActions.actionTypes.DO_IAM_LOGIN)
    yield call(iamLoginSaga, payload)
  }
}

function * watchGetCardHolderName () {
  while (true) {
    yield take(loginActions.actionTypes.DO_GET_CARDHOLDER_NAME)
    yield call(getCardHolderNameSaga)
  }
}

function * watchLogout () {
  while (true) {
    const { payload } = yield take(loginActions.actionTypes.DO_LOGOUT)
    yield fork(logoutSaga, payload)
  }
}

function * watchGetToken () {
  while (true) {
    const { payload } = yield take(loginActions.actionTypes.DO_GET_TOKEN)
    yield call(getTokenSaga, payload)
  }
}

function * watchUserLogin () {
  while (true) {
    const { payload } = yield take(loginActions.actionTypes.DO_USER_LOGIN)
    yield call(userLoginSaga, payload)
  }
}

function * watchChoseBiometricOption () {
  while (true) {
    const { payload } = yield take(loginActions.actionTypes.DO_BIOMETRIC_CHOICE)
    yield call(choseBiometricOptionSaga, payload)
  }
}

function * watchExtendSession () {
  while (true) {
    const { payload } = yield take(loginActions.actionTypes.DO_EXTEND_SESSION)
    yield call(extendSessionSaga, payload)
  }
}

function * watchGetLoginNews () {
  while (true) {
    const { payload } = yield take(loginActions.actionTypes.DO_GET_LOGIN_NEWS)
    yield call(getLoginNewsSaga, payload)
  }
}

/**
 * Root saga manages watcher lifecycle
 */
export default function * rootSaga () {
  yield all([
    watchUserLogin(),
    watchGetToken(),
    watchLogout(),
    watchGetCardHolderName(),
    watchIamLogin(),
    watchRopcLogin(),
    watchRefreshToken(),
    watchChoseBiometricOption(),
    watchExtendSession(),
    watchGetLoginNews()
    // @generator saga:fork
  ])
}
